import {defineStore} from 'pinia'
import {FilterMatchMode} from "primevue/api";
import _ from "lodash";
import {useAssetsStore} from "@/stores/assets.js";
import {getFirstPage, onFilter, onPage, onRowsUpdate, onSearch, onSort} from "@/stores/_helpers.js";

export const useAssetsDashboardPageStore = defineStore('assetsDashboardPage', {
    state: () => ({
        assets_count: {
            data: undefined,
            loading: true,
        },
        assets_without_devices_count: {
            data: undefined,
            loading: true,
        },
        assets_up_to_date_vs_needing_an_update: {
            data: undefined,
            loading: true,
        },
        assets_needing_an_update: {
            data: undefined,
            filters: null,
            totalRecords: 0,
            loading: true,
            params: {
                per_page: 10,
                page: 1,
                include: [],
                filter: {
                    search: undefined
                }
            },
        },
        assets_count_over_time: {
            data: undefined,
            loading: true,
        },

        pages: [
            10,
            20,
            50,
            100,
        ]
    }),
    getters: {
        getAssetsTotalCount: (state) => {
            return state.assets_count?.data;
        },
        getAssetsTotalCountLoading: (state) => {
            return state.assets_count?.loading;
        },

        getAssetsWithoutDevicesCount: (state) => {
            return state.assets_without_devices_count?.data;
        },
        getAssetsWithoutDevicesCountLoading: (state) => {
            return state.assets_without_devices_count?.loading;
        },

        getAssetsUpToDateVsNeedingAnUpdate: (state) => {
            return state.assets_up_to_date_vs_needing_an_update?.data;
        },
        getAssetsUpToDateVsNeedingAnUpdateLoading: (state) => {
            return state.assets_up_to_date_vs_needing_an_update?.loading;
        },

        getAssetsNeedingAnUpdate: (state) => {
            return state.assets_needing_an_update?.data;
        },
        getAssetsNeedingAnUpdateLoading: (state) => {
            return state.assets_needing_an_update?.loading;
        },

        getAssetsNeedingAnUpdateCount: (state) => {
            return state.assets_needing_an_update?.totalRecords;
        },
        getAssetsNeedingAnUpdateCountLoading: (state) => {
            return state.assets_needing_an_update?.loading;
        },

        getAssetsCountOverTime: (state) => {
            return state.assets_count_over_time?.data;
        },
        getAssetsCountOverTimeLoading: (state) => {
            return state.assets_count_over_time?.loading;
        },
    },
    actions: {
        getFirstPage: (key = 'assets_needing_an_update') => {
            return this?.[key] ? getFirstPage(this[key].params) : null;
        },
        onPage($event, key = 'assets_needing_an_update') {
            onPage($event, this[key].params);
            this.fetchAssetsNeedingAnUpdate();
        },

        async loadData() {
            const assetsStore = useAssetsStore();
            this.loading = true;
            this.data = await assetsStore.indexAssets(this.params)
            this.totalRecords = this.data.total;
            this.loading = false;
        },

        async fetchAssetCount(params = {}, forceRefresh = true) {
            if (forceRefresh)
                this.assets_count.data = undefined;

            if (this.assets_count.data === undefined){
                this.assets_count.loading = true;
                try {
                    const response = await axios.get('dashboard/assets/count', params);
                    this.assets_count.data = response.data;
                }
                catch (e) {
                    console.log(e);
                    this.assets_count.data = undefined;
                    throw (e);
                }
                finally {
                    this.assets_count.loading = false
                }
            }

            return this.assets_count.data;
        },
        async fetchAssetsWithoutDevicesCount(params = {}, forceRefresh = true) {
            if (forceRefresh)
                this.assets_without_devices_count.data = undefined;

            if (this.assets_without_devices_count.data === undefined){
                this.assets_without_devices_count.loading = true;
                try {
                    const response = await axios.get('dashboard/assets/without-devices-count', params);
                    this.assets_without_devices_count.data = response.data;
                }
                catch (e) {
                    console.log(e);
                    this.assets_without_devices_count.data = undefined;
                    throw (e);
                }
                finally {
                    this.assets_without_devices_count.loading = false
                }
            }

            return this.assets_without_devices_count.data;
        },
        async fetchAssetsUpToDateVsNeedingAnUpdate(params = {}, forceRefresh = true) {
            if (forceRefresh){
                this.assets_up_to_date_vs_needing_an_update.data = undefined;
            }

            if (this.assets_up_to_date_vs_needing_an_update.data === undefined){
                this.assets_up_to_date_vs_needing_an_update.loading = true;
                try {
                    const response = await axios.get('dashboard/assets/up-to-date-vs-needing-an-update', params);
                    this.assets_up_to_date_vs_needing_an_update.data = response.data;
                }
                catch (e) {
                    console.log(e);
                    this.assets_up_to_date_vs_needing_an_update.data = undefined;
                    throw (e);
                }
                finally {
                    this.assets_up_to_date_vs_needing_an_update.loading = false
                }
            }

            return this.assets_up_to_date_vs_needing_an_update.data;
        },
        async fetchAssetsNeedingAnUpdate(params = {}, forceRefresh = true) {
            if (forceRefresh){
                this.assets_needing_an_update.data = undefined;
                this.assets_needing_an_update.totalRecords = undefined;
            }

            if (this.assets_needing_an_update.data === undefined){
                this.assets_needing_an_update.loading = true;
                try {
                    const response = await axios.get('dashboard/assets/needing-an-update', {
                        params: this.assets_needing_an_update.params
                    });
                    this.assets_needing_an_update.data = response.data.data;
                    this.assets_needing_an_update.totalRecords = response.data.total;
                }
                catch (e) {
                    console.log(e);
                    this.assets_needing_an_update.data = undefined;
                    this.assets_needing_an_update.totalRecords = undefined;
                    throw (e);
                }
                finally {
                    this.assets_needing_an_update.loading = false
                }
            }

            return this.assets_needing_an_update.data;
        },
        async fetchAssetsCountOverTime(params = {}, forceRefresh = true) {
            if (forceRefresh)
                this.assets_count_over_time.data = undefined;

            if (this.assets_count_over_time.data === undefined){
                this.assets_count_over_time.loading = true;
                try {
                    const response = await axios.get('dashboard/assets/count-over-time', params);
                    this.assets_count_over_time.data = response.data;
                }
                catch (e) {
                    console.log(e);
                    this.assets_count_over_time.data = undefined;
                    throw (e);
                }
                finally {
                    this.assets_count_over_time.loading = false
                }
            }

            return this.assets_count_over_time.data;
        },
    },
});
